import React, { useState, useRef, useEffect } from 'react';
import { Database, Settings, LogOut, Users, X, ArrowRightLeft, Check, ArrowDownUp, Upload, Download, CheckCircle, XCircle, Pause, Play, ChevronUp, ChevronDown, Search, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { databasesApi } from '../../services/api';
import { isEntraAuth, graphPhotoScopes } from '../../auth/authConfig';
import { msalInstance } from '../../auth/authConfig';
import { useDataTransfer } from '../../context/DataTransferContext';
import { isDebugMode } from '../../utils/debug';
import logo from '../../assets/logos/WM-Logo-CB-White-Header.png';

const Header = ({ onNavigateHome, setCurrentPage }) => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { currentUser, isImpersonating, hasPersistedIdentity, users, isAdmin, isSysAdmin, access_Group, roleInDb, impersonate, stopImpersonating, claimIdentity } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const { activities, pauseActivity, restartActivity, reorderQueue, clearActivity, clearAllCompleted, clearMode, setClearMode } = useDataTransfer();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showDbSwitcherModal, setShowDbSwitcherModal] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [impersonateSearch, setImpersonateSearch] = useState('');
  // Local DB list for the switcher modal — fetched independently so it works on admin pages too
  const [switcherDatabases, setSwitcherDatabases] = useState([]);
  const [availableDbCount, setAvailableDbCount] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setAvailableDbCount((res.data || []).length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEntraAuth() || !msalInstance) return;
    const account = msalInstance.getAllAccounts()[0];
    if (!account) return;
    let objectUrl = null;
    const req = { ...graphPhotoScopes, account };

    const fetchPhoto = (token) =>
      fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.blob() : null)
      .then(blob => { if (blob) { objectUrl = URL.createObjectURL(blob); setAvatarUrl(objectUrl); } });

    msalInstance.acquireTokenSilent(req)
      .then(r => fetchPhoto(r.accessToken))
      .catch(() => {});

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, []);

  const userDropdownRef = useRef(null);
  const impersonateRef = useRef(null);
  const dbSwitcherRef = useRef(null);
  const activityPanelRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (impersonateRef.current && !impersonateRef.current.contains(event.target)) {
        setShowImpersonateModal(false);
      }
      if (dbSwitcherRef.current && !dbSwitcherRef.current.contains(event.target)) {
        setShowDbSwitcherModal(false);
      }
      if (activityPanelRef.current && !activityPanelRef.current.contains(event.target)) {
        setShowActivityPanel(false);
        setActivitySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePreferences = () => {
    setShowUserDropdown(false);
    setCurrentPage('preferences');
  };

  const handleSignOut = () => {
    setShowUserDropdown(false);
    if (isEntraAuth() && msalInstance) {
      msalInstance.logoutRedirect();
    } else {
      console.log('Sign out clicked');
    }
  };

  const handleImpersonate = () => {
    setShowUserDropdown(false);
    setImpersonateSearch('');
    setShowImpersonateModal(true);
  };

  const handleSwitchDatabase = () => {
    setShowUserDropdown(false);
    // Fetch databases fresh for the modal (works on both data and admin pages)
    databasesApi.getAvailable()
      .then(res => setSwitcherDatabases(res.data || []))
      .catch(() => setSwitcherDatabases([]));
    setShowDbSwitcherModal(true);
  };

  const handleSelectUser = (user) => {
    impersonate(user);
    setShowImpersonateModal(false);
  };

  const handleSelectDatabase = (db) => {
    const displayName = db.displayName || db.name;
    // URL slug from db.slug (the Slug column). Fallback to displayName.toLowerCase for safety.
    const slug = db.slug || displayName.toLowerCase();
    const dbName = db.databaseName || db.id;
    // Update localStorage
    localStorage.setItem('pmd-selected-database', dbName);
    localStorage.setItem('pmd-selected-db-slug', slug);
    window.dispatchEvent(new Event('pmd-database-changed'));
    // Navigate to the new DB home
    navigate(`/${slug}/`);
    setShowDbSwitcherModal(false);
  };

  // Sort users: current user first, then alphabetically; filtered by search
  const sortedUsers = [...users]
    .sort((a, b) => {
      if (a.id === currentUser?.id) return -1;
      if (b.id === currentUser?.id) return 1;
      return (a.user_Name || '').localeCompare(b.user_Name || '');
    })
    .filter(u => !impersonateSearch || (u.user_Name || '').toLowerCase().includes(impersonateSearch.toLowerCase()));

  return (
    <header className="stui-header">
      <div className="stui-header-left">
        <img
          src={logo}
          alt="WM Logo"
          className="stui-header-logo"
          onClick={onNavigateHome}
        />
        <span className="stui-header-title">DAWN</span>
      </div>
      {dbContext?.currentDb?.logoUrl && (
        <div className="stui-header-center">
          <img src={dbContext.currentDb.logoUrl} alt="" className="stui-header-org-logo" />
        </div>
      )}
      <div className="stui-header-right">
        <div className="stui-header-user-info">
          {isImpersonating ? (
            <div className="stui-header-imp-area">
              <div className="stui-header-imp-icons">
                {/* Green check — "This is me" (claim identity, persists) */}
                {(!isEntraAuth()) && (
                  <button
                    className="stui-header-imp-btn stui-header-imp-claim"
                    onClick={() => claimIdentity()}
                    title="This is me — remember for next session"
                  >
                    <Check size={13} />
                  </button>
                )}
                {/* Red X — "Go back" (revert to identity) */}
                <button
                  className="stui-header-imp-btn stui-header-imp-revert"
                  onClick={stopImpersonating}
                  title="Stop impersonating"
                >
                  <X size={13} />
                </button>
              </div>
              <div className="stui-header-imp-text">
                <span className="stui-header-user-name" style={{ color: '#fca5a5', fontWeight: 600 }}>
                  {currentUser?.user_Name || 'Unknown'}
                </span>
                <span className="stui-header-user-group" style={{ color: '#fca5a5' }}>
                  {dbSlug ? roleInDb(dbSlug) : access_Group}
                </span>
              </div>
            </div>
          ) : (
            <>
              <span className="stui-header-user-name">{currentUser?.user_Name || 'Loading...'}</span>
              <span className="stui-header-user-group">{dbSlug ? roleInDb(dbSlug) : access_Group}</span>
            </>
          )}
        </div>
        {/* ── Data Transfer Icon ── */}
        <div className="stui-activity-wrapper" ref={activityPanelRef}>
          <button
            className={`stui-activity-icon${activities.some(a => a.status === 'active') ? ' active' : ''}`}
            onClick={() => { setShowActivityPanel(p => !p); setActivitySearch(''); }}
            title="Data Transfer"
          >
            <ArrowDownUp size={20} />
            {activities.length > 0 && (
              <span className={`stui-activity-badge ${activities.some(a => a.status === 'active') ? 'stui-activity-badge-active' : 'stui-activity-badge-complete'}`}>
                {activities.length}
              </span>
            )}
          </button>

          {showActivityPanel && (
            <ActivityPanel
              activities={activities}
              search={activitySearch}
              setSearch={setActivitySearch}
              clearMode={clearMode}
              setClearMode={setClearMode}
              onPause={pauseActivity}
              onRestart={restartActivity}
              onReorder={reorderQueue}
              onDismiss={clearActivity}
              onClearAll={clearAllCompleted}
              onHistoryNav={() => {
                setShowActivityPanel(false);
                const slug = localStorage.getItem('pmd-selected-db-slug') || 'gwa';
                navigate(`/${slug}/data-transfer-history`);
              }}
            />
          )}
        </div>

        <div className="stui-header-user-menu" ref={userDropdownRef}>
          <button
            className="stui-header-user-icon"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <UserInitialsAvatar name={msalInstance?.getAllAccounts()[0]?.name || currentUser?.user_Name} />
            )}
          </button>
          {showUserDropdown && (
            <div className="stui-header-user-dropdown">
              {availableDbCount > 1 && (
                <>
                  <button className="stui-header-user-option" onClick={handleSwitchDatabase}>
                    <ArrowRightLeft size={16} />
                    <span>Switch Database</span>
                  </button>
                  <div className="stui-header-dropdown-divider" />
                </>
              )}
              {(!isEntraAuth() || isSysAdmin) && (
                <>
                  <button className="stui-header-user-option" onClick={handleImpersonate}>
                    <Users size={16} />
                    <span>Impersonate User</span>
                  </button>
                  <div className="stui-header-dropdown-divider" />
                </>
              )}
              <button className="stui-header-user-option" onClick={handlePreferences}>
                <Settings size={16} />
                <span>Preferences</span>
              </button>
              <div className="stui-header-dropdown-divider" />
              <button className="stui-header-user-option" onClick={handleSignOut}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Impersonate Modal */}
      {showImpersonateModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal" ref={impersonateRef}>
            <div className="stui-modal-header">
              <h3>Impersonate User</h3>
              <button className="stui-modal-close" onClick={() => setShowImpersonateModal(false)}>&times;</button>
            </div>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-gray-200)' }}>
              <input
                className="stui-field-input"
                placeholder="Search users..."
                value={impersonateSearch}
                onChange={e => setImpersonateSearch(e.target.value)}
                autoFocus
                style={{ width: '100%' }}
              />
            </div>
            <div className="stui-modal-list">
              {sortedUsers.map(user => (
                <button
                  key={user.id}
                  className={`stui-modal-user ${user.id === currentUser?.id ? 'current' : ''}`}
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="stui-modal-user-info">
                    <span className="stui-modal-user-name">{user.user_Name}</span>
                    <span className="stui-modal-user-group">{user.organizationName || user.access_Group}</span>
                  </div>
                  {user.id === currentUser?.id && (
                    <span className="stui-modal-current-badge">Current</span>
                  )}
                </button>
              ))}
              {sortedUsers.length === 0 && (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)' }}>No users match</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Database Switcher Modal */}
      {showDbSwitcherModal && (
        <div className="stui-modal-overlay">
          <div className="stui-db-switcher-modal" ref={dbSwitcherRef}>
            <div className="stui-db-switcher-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', flex: 1, minWidth: 0 }}>
                <img src={logo} alt="Wunderlich-Malec" style={{ height: '32px', flexShrink: 0 }} />
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '0.875rem' }}>
                  <div style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.1 }}>DAWN</div>
                  <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem', letterSpacing: '0.04em', lineHeight: 1.2, marginTop: '2px' }}>
                    Documentation, Assets, Wiring &amp; Networking
                  </div>
                </div>
              </div>
              <button
                className="stui-modal-close"
                onClick={() => setShowDbSwitcherModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="stui-db-switcher-list">
              {(isSysAdmin ? switcherDatabases : switcherDatabases.filter(db => {
                const slug = db.slug || (db.displayName || db.name)?.toLowerCase();
                return slug && currentUser?.roles?.[slug];
              })).map(db => {
                const name = db.displayName || db.name;
                const dbSlugKey = db.slug || name?.toLowerCase();
                const isActive = dbSlugKey === dbSlug;
                return (
                  <button
                    key={db.id || db.databaseName}
                    className={`stui-db-switcher-row ${isActive ? 'current' : ''}`}
                    onClick={() => handleSelectDatabase(db)}
                  >
                    <div className="stui-db-switcher-logo">
                      {db.iconUrl ? (
                        <img src={db.iconUrl} alt="" />
                      ) : (
                        <div className="stui-db-switcher-logo-placeholder">
                          <Database size={16} />
                        </div>
                      )}
                    </div>
                    <div className="stui-db-switcher-info">
                      <span className="stui-db-switcher-name">{name}</span>
                      {db.description && (
                        <span className="stui-db-switcher-desc">{db.description}</span>
                      )}
                    </div>
                    {isActive && (
                      <span className="stui-db-switcher-badge">Current</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// ── UserInitialsAvatar ────────────────────────────────────────────────────────
const DAWN_AVATAR_COLORS = [
  { bg: '#1a5568', text: '#ffffff' },
  { bg: '#2d7a9a', text: '#ffffff' },
  { bg: '#1e6b5a', text: '#ffffff' },
  { bg: '#3d5a80', text: '#ffffff' },
  { bg: '#4a5568', text: '#ffffff' },
  { bg: '#2c5f6e', text: '#ffffff' },
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return DAWN_AVATAR_COLORS[0];
  const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return DAWN_AVATAR_COLORS[hash % DAWN_AVATAR_COLORS.length];
};

const UserInitialsAvatar = ({ name }) => {
  const { bg, text } = getAvatarColor(name);
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: bg, color: text,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: 700, fontFamily: 'Rajdhani, Arial, sans-serif',
      letterSpacing: '0.5px', flexShrink: 0, userSelect: 'none'
    }}>
      {getInitials(name)}
    </div>
  );
};

// ── ActivityPanel ─────────────────────────────────────────────────────────────
const CLEAR_OPTIONS = [
  { label: '1m',    value: 'auto-1m' },
  { label: '1 day', value: 'auto-1d' },
  { label: '1 wk',  value: 'auto-1w' },
  { label: '1 mo',  value: 'auto-1mo' },
];

const FORMAT_COLORS = {
  pdf:    { bg: '#fee2e2', color: '#dc2626' },
  excel:  { bg: '#dcfce7', color: '#16a34a' },
  csv:    { bg: '#dbeafe', color: '#1d4ed8' },
  import: { bg: '#fef9c3', color: '#854d0e' },
};

const ActivityPanel = ({ activities, search, setSearch, clearMode, setClearMode, onPause, onRestart, onReorder, onDismiss, onClearAll, onHistoryNav }) => {
  const debug = isDebugMode();
  const q = search.toLowerCase();
  const filtered = q
    ? activities.filter(a => a.name.toLowerCase().includes(q) || a.format.toLowerCase().includes(q))
    : activities;

  const local    = filtered.filter(a => a.source === 'local');
  const otherTab = filtered.filter(a => a.source === 'other-tab');
  const remote   = filtered.filter(a => a.source === 'remote');

  // Group remote by machineName
  const remoteByMachine = remote.reduce((acc, a) => {
    const key = a.machineName || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  const hasCompleted = activities.some(a => a.status === 'complete' || a.status === 'failed');

  return (
    <div className="stui-activity-panel">
      {/* Panel header */}
      <div className="stui-activity-panel-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ArrowDownUp size={14} style={{ color: 'var(--color-primary)' }} />
          <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--color-gray-700)' }}>Data Transfer</span>
        </div>
        {hasCompleted && (
          <button className="stui-activity-clear-all-btn" onClick={onClearAll} title="Clear all completed">
            Clear Completed
          </button>
        )}
      </div>

      {/* Search */}
      <div className="stui-activity-search-row">
        <Search size={12} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />
        <input
          className="stui-activity-search-input"
          placeholder="Filter transfers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Entry list */}
      <div className="stui-activity-entries-scroll">
        {local.length === 0 && otherTab.length === 0 && Object.keys(remoteByMachine).length === 0 && (
          <div className="stui-activity-empty">No active transfers</div>
        )}

        {local.map(a => (
          <ActivityEntry key={a.id} activity={a} debug={debug} isLocal
            onPause={() => onPause(a.id)}
            onRestart={() => onRestart(a.id)}
            onReorderUp={() => onReorder(a.id, 'up')}
            onReorderDown={() => onReorder(a.id, 'down')}
            onDismiss={clearMode === 'manual' ? () => onDismiss(a.id) : null}
          />
        ))}

        {otherTab.length > 0 && (
          <>
            <div className="stui-activity-source-header">This Machine</div>
            {otherTab.map(a => <ActivityEntry key={a.id} activity={a} debug={debug} />)}
          </>
        )}

        {Object.entries(remoteByMachine).map(([machine, entries]) => (
          <React.Fragment key={machine}>
            <div className="stui-activity-source-header">Remote · {machine}</div>
            {entries.map(a => <ActivityEntry key={a.id} activity={a} debug={debug} />)}
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div className="stui-activity-panel-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '9px', color: 'var(--color-gray-400)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Clear:</span>
          {CLEAR_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setClearMode(opt.value)}
              className={`stui-activity-clear-pill${clearMode === opt.value ? ' selected' : ''}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button className="stui-activity-history-link" onClick={onHistoryNav}>
          <Clock size={11} /> History
        </button>
      </div>
    </div>
  );
};

const ActivityEntry = ({ activity: a, debug, isLocal, onPause, onRestart, onReorderUp, onReorderDown, onDismiss }) => {
  const fmtColor = FORMAT_COLORS[a.format] || { bg: 'var(--color-gray-100)', color: 'var(--color-gray-600)' };
  const isActive  = a.status === 'active';
  const isPaused  = a.status === 'paused';
  const isQueued  = a.status === 'queued';
  const isDone    = a.status === 'complete';
  const isFailed  = a.status === 'failed';

  return (
    <div className={`stui-activity-entry${isDone ? ' done' : ''}${isFailed ? ' failed' : ''}`}>
      <div className="stui-entry-icon-col">
        {a.type === 'upload'
          ? <Upload size={13} style={{ color: 'var(--color-primary)' }} />
          : <Download size={13} style={{ color: '#16a34a' }} />
        }
      </div>
      <div className="stui-entry-main-col">
        <div className="stui-entry-name-row">
          <span className="stui-entry-name">{a.name}</span>
          <span className="stui-entry-format-badge" style={{ background: fmtColor.bg, color: fmtColor.color }}>
            {a.format.toUpperCase()}
          </span>
          {debug && (
            <span style={{ fontSize: '9px', color: 'var(--color-gray-400)', marginLeft: '4px' }}>
              {a.pct}% · {a.source}
            </span>
          )}
        </div>
        <div className="stui-entry-phase">{isQueued ? `Queued` : a.phase}</div>
        {(isActive || isPaused || isQueued) && (
          <div className="stui-entry-progress-track">
            <div className="stui-entry-progress-fill" style={{ width: `${Math.max(2, a.pct)}%`, background: isPaused ? 'var(--color-gray-300)' : isQueued ? 'var(--color-gray-200)' : 'var(--color-primary)' }} />
          </div>
        )}
      </div>
      <div className="stui-entry-actions-col">
        {isDone && <CheckCircle size={14} style={{ color: '#16a34a' }} />}
        {isFailed && <XCircle size={14} style={{ color: '#dc2626' }} />}
        {isLocal && isActive && onPause && (
          <button className="stui-entry-action-btn" onClick={onPause} title="Pause"><Pause size={11} /></button>
        )}
        {isLocal && isPaused && onRestart && (
          <button className="stui-entry-action-btn" onClick={onRestart} title="Restart"><Play size={11} /></button>
        )}
        {isLocal && isQueued && (
          <>
            {onReorderUp && <button className="stui-entry-action-btn" onClick={onReorderUp} title="Move up"><ChevronUp size={11} /></button>}
            {onReorderDown && <button className="stui-entry-action-btn" onClick={onReorderDown} title="Move down"><ChevronDown size={11} /></button>}
          </>
        )}
        {(isDone || isFailed) && onDismiss && (
          <button className="stui-entry-action-btn" onClick={onDismiss} title="Dismiss"><X size={11} /></button>
        )}
        {(isDone) && a.exportRecipe && !isLocal && (
          <button className="stui-entry-redownload-btn" title="Re-download" onClick={() => {
            try {
              const recipe = typeof a.exportRecipe === 'string' ? JSON.parse(a.exportRecipe) : a.exportRecipe;
              window.location.href = recipe.reportPath + '?autoExport=' + recipe.format;
            } catch {}
          }}>↓</button>
        )}
      </div>
    </div>
  );
};

export default Header;
